import React, { useEffect } from 'react';
import { Prospect } from '../../shared/types';
import { clinicaProfissionais } from '../../shared/data/clinicaData';
import { Calendar, Search, Bell, Menu, Plus, Clock, UserCheck, FileText, CheckCircle2, ChevronRight, MessageSquare } from 'lucide-react';

export const HealthSystem: React.FC<{ prospect: Prospect }> = ({ prospect }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans flex overflow-hidden selection:bg-[var(--ym-prospect-primary)] selection:text-white">
      
      {/* Sidebar Medical */}
      <aside className="w-20 md:w-64 bg-white border-r border-slate-200 flex flex-col h-screen shrink-0">
         <div className="h-16 flex items-center justify-center md:justify-start md:px-6 border-b border-slate-100 shrink-0">
            <div className="w-8 h-8 rounded-lg bg-[var(--ym-prospect-primary)]/10 text-[var(--ym-prospect-primary)] flex items-center justify-center">
               <span className="font-bold text-lg leading-none">+</span>
            </div>
            <span className="ml-3 font-heading font-black text-lg hidden md:block">HealthSys</span>
         </div>
         <div className="p-4 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4 hidden md:block px-2">Gestão Clínica</div>
            <nav className="space-y-2">
               <button className="w-full flex items-center justify-center md:justify-start gap-3 px-3 py-2 bg-[var(--ym-prospect-primary)]/10 text-[var(--ym-prospect-primary)] rounded-xl font-bold transition-colors">
                  <Calendar className="w-5 h-5" /> <span className="hidden md:block">Agenda do Dia</span>
               </button>
               <button className="w-full flex items-center justify-center md:justify-start gap-3 px-3 py-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 rounded-xl font-medium transition-colors">
                  <UserCheck className="w-5 h-5" /> <span className="hidden md:block">Pacientes</span>
               </button>
               <button className="w-full flex items-center justify-center md:justify-start gap-3 px-3 py-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 rounded-xl font-medium transition-colors">
                  <FileText className="w-5 h-5" /> <span className="hidden md:block">Prontuários</span>
               </button>
            </nav>
         </div>
         <div className="p-4 border-t border-slate-100 flex items-center justify-center md:justify-start gap-3">
            <img src={clinicaProfissionais[0].photo} className="w-10 h-10 rounded-full object-cover" alt="Doctor" />
            <div className="hidden md:block">
               <p className="text-sm font-bold text-slate-900 line-clamp-1">{clinicaProfissionais[0].name}</p>
               <p className="text-[10px] text-slate-500">{clinicaProfissionais[0].specialty}</p>
            </div>
         </div>
      </aside>

      {/* Main Area */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden">
         
         {/* Header */}
         <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 shrink-0 shadow-sm">
            <div className="flex items-center gap-4">
               <button className="md:hidden"><Menu className="w-5 h-5 text-slate-600" /></button>
               <h2 className="font-heading font-bold text-xl text-slate-900 hidden sm:block">Agenda: 15 de Outubro</h2>
            </div>
            <div className="flex items-center gap-4">
               <div className="relative hidden md:block">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input type="text" placeholder="Buscar paciente por nome ou CPF..." className="pl-9 pr-4 py-2 bg-slate-100 rounded-full text-sm outline-none w-64 border-none focus:ring-2 focus:ring-[var(--ym-prospect-primary)]/20 transition-all" />
               </div>
               <button className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 relative hover:bg-slate-200 transition-colors">
                  <Bell className="w-5 h-5" />
                  <span className="absolute top-2 right-2 w-2 h-2 bg-rose-500 rounded-full"></span>
               </button>
               <button className="bg-[var(--ym-prospect-primary)] text-white px-4 py-2 rounded-full text-sm font-bold shadow-md hover:opacity-90 flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Novo Agendamento
               </button>
            </div>
         </header>

         {/* Board de Atendimento */}
         <div className="flex-1 p-6 overflow-hidden flex gap-6">
            
            {/* Coluna Agenda */}
            <div className="w-full lg:w-[400px] flex flex-col bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden shrink-0">
               <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                  <h3 className="font-bold text-slate-800">Próximos Pacientes</h3>
                  <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-1 rounded-md">8 Hoje</span>
               </div>
               <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  
                  {/* Card Paciente Ativo */}
                  <div className="border-2 border-[var(--ym-prospect-primary)] bg-[var(--ym-prospect-primary)]/5 rounded-2xl p-4 shadow-sm cursor-pointer relative overflow-hidden">
                     <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[var(--ym-prospect-primary)]"></div>
                     <div className="flex justify-between items-start mb-2">
                        <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-[var(--ym-prospect-primary)]"><Clock className="w-3 h-3" /> 14:30 (Agora)</span>
                        <span className="bg-white text-[var(--ym-prospect-primary)] text-[10px] font-bold px-2 py-0.5 rounded shadow-sm">Retorno</span>
                     </div>
                     <h4 className="font-bold text-slate-900">Mariana Costa Silva</h4>
                     <p className="text-xs text-slate-500 mb-3">Avaliação Invisalign - 2ª Sessão</p>
                     <div className="flex gap-2">
                        <span className="bg-white px-2 py-1 rounded text-[10px] font-bold text-slate-600 border border-slate-200 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500"/> Chegou</span>
                     </div>
                  </div>

                  {/* Card Paciente Aguardando */}
                  <div className="border border-slate-200 bg-white rounded-2xl p-4 hover:border-[var(--ym-prospect-primary)]/50 transition-colors cursor-pointer">
                     <div className="flex justify-between items-start mb-2">
                        <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-slate-500"><Clock className="w-3 h-3" /> 15:30</span>
                        <span className="bg-slate-100 text-slate-600 text-[10px] font-bold px-2 py-0.5 rounded">Primeira Consulta</span>
                     </div>
                     <h4 className="font-bold text-slate-900">Carlos Eduardo Alves</h4>
                     <p className="text-xs text-slate-500 mb-3">Clareamento a Laser</p>
                     <div className="flex gap-2">
                        <span className="bg-amber-50 px-2 py-1 rounded text-[10px] font-bold text-amber-600 border border-amber-100">Atrasado 10min</span>
                     </div>
                  </div>

                  <div className="border border-slate-200 bg-white rounded-2xl p-4 hover:border-[var(--ym-prospect-primary)]/50 transition-colors cursor-pointer">
                     <div className="flex justify-between items-start mb-2">
                        <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-slate-500"><Clock className="w-3 h-3" /> 16:30</span>
                     </div>
                     <h4 className="font-bold text-slate-900">Ana Beatriz Mendes</h4>
                     <p className="text-xs text-slate-500 mb-3">Implante - Avaliação</p>
                     <div className="flex gap-2">
                        <span className="bg-slate-50 px-2 py-1 rounded text-[10px] font-bold text-slate-400 border border-slate-200">Aguardando Confirmação</span>
                     </div>
                  </div>
               </div>
            </div>

            {/* Prontuário Eletrônico (Painel Direito) */}
            <div className="hidden lg:flex flex-1 flex-col bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
               <div className="p-6 border-b border-slate-100 flex justify-between items-start">
                  <div className="flex gap-4 items-center">
                     <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=200&auto=format&fit=crop" className="w-16 h-16 rounded-2xl object-cover" alt="Paciente" />
                     <div>
                        <h2 className="text-2xl font-bold font-heading text-slate-900">Mariana Costa Silva</h2>
                        <p className="text-sm text-slate-500">28 anos • Feminino • Convênio: Unimed VIP</p>
                     </div>
                  </div>
                  <button className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-[var(--ym-prospect-primary)] hover:bg-slate-100 transition-colors">
                     <MessageSquare className="w-5 h-5" />
                  </button>
               </div>
               
               <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
                  <div className="grid grid-cols-2 gap-6 mb-6">
                     <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Alergias</h4>
                        <p className="text-sm font-bold text-rose-500">Dipirona, Penicilina</p>
                     </div>
                     <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Última Visita</h4>
                        <p className="text-sm font-bold text-slate-700">10 de Setembro, 2026</p>
                     </div>
                  </div>

                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-[300px]">
                     <div className="bg-slate-50 border-b border-slate-100 p-3 flex gap-4">
                        <button className="text-sm font-bold text-[var(--ym-prospect-primary)] border-b-2 border-[var(--ym-prospect-primary)] px-2 pb-1">Anamnese de Hoje</button>
                        <button className="text-sm font-bold text-slate-400 hover:text-slate-600 px-2 pb-1">Histórico</button>
                        <button className="text-sm font-bold text-slate-400 hover:text-slate-600 px-2 pb-1">Prescrições</button>
                     </div>
                     <div className="p-4 flex-1">
                        <textarea 
                           className="w-full h-full border-none outline-none resize-none text-sm text-slate-700 placeholder:text-slate-300 bg-transparent"
                           placeholder="Digite a evolução médica ou observações clínicas do atendimento..."
                           defaultValue="Paciente relata melhora na sensibilidade pós-instalação dos alinhadores. Nenhuma queixa de dor na mastigação."
                        ></textarea>
                     </div>
                     <div className="p-3 border-t border-slate-100 bg-slate-50 flex justify-end">
                        <button className="bg-[var(--ym-prospect-primary)] text-white px-6 py-2 rounded-xl text-sm font-bold shadow-md hover:opacity-90">
                           Salvar Evolução
                        </button>
                     </div>
                  </div>
               </div>
            </div>

         </div>

      </main>

      <div className="fixed bottom-4 right-4 bg-slate-900 text-white px-4 py-2 rounded-lg text-xs font-bold shadow-2xl opacity-50 z-50">
        Demo do Sistema/Prontuário Médico
      </div>
    </div>
  );
};
